import { Snapshot } from './diff-snapshots';
import { ActivityEntityName } from '../../services/apollo/inopack-graphql-schema';
import {
    lookupCollectionLabel,
    lookupEntityFieldLabel,
    lookupFieldLabel,
} from '../labels';

// Display layer for the audit diff. Everything here is OPTIONAL polish: an
// unlabelled field degrades to its humanised column name rather than
// disappearing, so the dialog is useful before a single label is written. That
// asymmetry is deliberate — a missing label is visible and harmless, whereas a
// missing field in an audit log is a silent lie.
//
// The Spanish words themselves live in `src/helpers/labels/` — this file is a
// CONSUMER of that dictionary, not its owner. They moved there because the audit
// was one of three independent lists of Spanish strings for the same columns
// (the others being form inputs and table headers), and three lists always
// drift. See docs/features/ongoing/feature-activities-audit.md §10.
//
// The audit always reads the `long` variant: a diff row lists fields of mixed
// provenance in one column with no headings, so each one has to stand alone.

/**
 * How to read a child row's display name out of the snapshot, keyed by the
 * collection it came from.
 *
 * Each snapshot denormalises just enough of the related row to label the line —
 * `products` on a sale line, `resources` on an expense line, the applied sale or
 * expense on a transfer receipt — so the name shown is the one that was current
 * when the change happened. An unmapped collection falls back to `#<id>`, which
 * is why adding an entity needs no entry here in order to work.
 */
const ROW_LABEL_READERS: Record<
    string,
    (child: Record<string, any>) => unknown
> = {
    order_sale_products: (child) => child.products?.description,
    expense_resources: (child) => child.resources?.name,
    // A receipt is applied to exactly one of the two. `||` rather than `??`
    // because external_code defaults to '' rather than null.
    transfer_receipts: (child) =>
        child.order_sales?.order_code ||
        child.expenses?.external_code ||
        child.expenses?.internal_code,
    order_request_products: (child) => child.products?.description,
    order_adjustment_products: (child) => child.products?.description,
    order_production_products: (child) => child.products?.description,
    order_production_products_consumed: (child) => child.products?.description,
    order_production_employees: (child) => child.employees?.fullname,
    account_products: (child) => child.products?.description,
    account_resources: (child) => child.resources?.name,
    // account_contacts has no related row to denormalise; its own column is the
    // label.
    account_contacts: (child) => child.fullname,
    user_roles: (child) => child.roles?.name,
};

/**
 * Columns that hold a foreign key render as a raw number — `Status | 2 | 1` —
 * which is unreadable in an audit. The label comes from the related row that
 * the snapshot denormalised beside it, EXACTLY like a child line's product:
 * captured at write time, so an old entry shows the name as it was.
 *
 * Two candidate relation keys are derived from the column name, because Prisma
 * names a relation after its target model and this schema uses both spellings:
 * `transfer_type_id` -> `transfer_type`, `order_sale_status_id` ->
 * `order_sale_statuses`.
 */
function relationKeyCandidates(column: string): string[] {
    const base = column.slice(0, -3);
    if (!base) return [];

    let plural: string;
    if (/y$/.test(base)) plural = base.slice(0, -1) + 'ies';
    else if (/(s|ch|sh|x|z)$/.test(base)) plural = base + 'es';
    else plural = base + 's';

    return [base, plural];
}

/**
 * Which column of a related row is its name.
 *
 * Order matters only where a snapshot selects more than one candidate: a
 * product carries both `code` and `description`, and `description` is what the
 * child-row labels already show. Each snapshot `select`s narrowly for exactly
 * this reason — the fewer columns arrive, the less there is to guess between.
 */
const RELATION_LABEL_COLUMNS = [
    'fullname',
    'name',
    'description',
    'order_code',
    'external_code',
    'internal_code',
    'code',
    'abbreviation',
];

function readRelationLabel(related: Record<string, any>): string | undefined {
    for (let i = 0; i < RELATION_LABEL_COLUMNS.length; i++) {
        const value = related[RELATION_LABEL_COLUMNS[i]];
        if (value === null || value === undefined) continue;
        const text = String(value);
        // external_code defaults to '' rather than null, so empty is "absent".
        if (text !== '') return text;
    }
    return undefined;
}

/** The name behind each side of a foreign-key change, keyed by identity path. */
export interface ValueLabelPair {
    old?: string;
    new?: string;
}

/**
 * Builds `{ "order_sale_status_id": { old: 'Entregado', new: 'Cotización' } }`
 * by walking BOTH raw snapshots the same way `flattenSnapshot` does, so the
 * keys line up with the diff's identity paths — head fields and child fields
 * alike (`order_sale_products.754.product_id`).
 *
 * A label is only used when the derived relation key exists AND the related
 * row's `id` equals the foreign key's value. Requiring both is what makes this
 * safe without per-column configuration: catalogue ids are small and collide
 * constantly (`branch_id: 3` and `employee_id: 3` on the same row), so matching
 * on the name alone, or on the value alone, would confidently show the wrong
 * name. When they disagree — or the relation was never snapshotted — the id is
 * left to render as itself, which is visible rather than wrong.
 */
export function buildValueLabels(
    oldData: Snapshot,
    newData: Snapshot,
): Record<string, ValueLabelPair> {
    const labels: Record<string, ValueLabelPair> = {};

    const assign = (path: string, side: 'old' | 'new', label: string) => {
        if (!labels[path]) labels[path] = {};
        labels[path][side] = label;
    };

    const collect = (row: Snapshot, prefix: string, side: 'old' | 'new') => {
        if (!row) return;
        const keys = Object.keys(row);

        for (let i = 0; i < keys.length; i++) {
            const key = keys[i];
            const value = (row as Record<string, unknown>)[key];

            if (Array.isArray(value)) {
                for (let j = 0; j < value.length; j++) {
                    const child = value[j] as Record<string, any> | null;
                    if (!child || typeof child !== 'object') continue;
                    if (child.id === undefined || child.id === null) continue;
                    collect(child, `${prefix}${key}.${child.id}.`, side);
                }
                continue;
            }

            if (key.length <= 3 || key.slice(-3) !== '_id') continue;
            if (value === null || value === undefined) continue;
            if (typeof value === 'object') continue;

            const candidates = relationKeyCandidates(key);
            for (let c = 0; c < candidates.length; c++) {
                const related = (row as Record<string, unknown>)[candidates[c]];
                if (!related || typeof related !== 'object') continue;
                if (Array.isArray(related)) continue;
                // Both the name and the id must agree before a label is used.
                if ((related as { id?: unknown }).id !== value) continue;

                const label = readRelationLabel(related as Record<string, any>);
                if (label) assign(`${prefix}${key}`, side, label);
                break;
            }
        }
    };

    collect(oldData, '', 'old');
    collect(newData, '', 'new');

    return labels;
}

// Keys already reported, so a table of 40 rows warns at most once per column.
const warnedKeys: Record<string, boolean> = {};

/**
 * Last-resort rendering for a column nobody has labelled.
 *
 * The result is the raw DB column name, which means ENGLISH inside a Spanish
 * table. That is deliberate — a visible gap is better than a hidden one, and it
 * is the reason the labels were not simply switched to English (§9.4). In
 * development it also warns, which is the only check that can catch a column
 * added to the schema after `scripts/check-activity-labels.cjs` last ran.
 */
function humanize(key: string): string {
    if (process.env.NODE_ENV === 'development' && !warnedKeys[key]) {
        warnedKeys[key] = true;
        // eslint-disable-next-line no-console
        console.warn(
            `[audit] Sin etiqueta en español para la columna "${key}" — ` +
                'añádela a FIELD_LABELS en src/helpers/labels/field-labels.ts.',
        );
    }

    return key.replace(/_/g, ' ');
}

/**
 * Builds `{ "collection.rowId": "BOLSA 20x30" }` from either snapshot, so a
 * child change reads "Producto BOLSA 20x30 · Precio por kilo" instead of
 * "order_sale_products #754 · kilo price".
 *
 * Reads BOTH sides because a removed line only exists in `old_data` and an added
 * one only in `new_data`.
 */
export function buildRowLabels(
    oldData: Snapshot,
    newData: Snapshot,
): Record<string, string> {
    const labels: Record<string, string> = {};

    const collect = (row: Snapshot) => {
        if (!row) return;
        const keys = Object.keys(row);
        for (let i = 0; i < keys.length; i++) {
            const value = (row as Record<string, unknown>)[keys[i]];
            if (!Array.isArray(value)) continue;

            for (let j = 0; j < value.length; j++) {
                const child = value[j] as Record<string, any> | null;
                if (!child || typeof child !== 'object') continue;
                if (child.id === undefined || child.id === null) continue;

                // The related row is denormalised into the snapshot precisely so
                // the label reflects the name AS IT WAS at the time.
                const readLabel = ROW_LABEL_READERS[keys[i]];
                if (!readLabel) continue;

                const label = readLabel(child);
                if (label) {
                    labels[`${keys[i]}.${child.id}`] = String(label);
                }
            }
        }
    };

    collect(oldData);
    collect(newData);

    return labels;
}

/** Turns an identity path into something a human can read. */
export function describeChangePath(
    path: string,
    rowLabels: Record<string, string> = {},
    entityName?: ActivityEntityName | null,
): string {
    const segments = path.split('.');
    const field = segments[segments.length - 1];
    const fieldLabel =
        lookupEntityFieldLabel(entityName, field)?.long ??
        lookupFieldLabel(field)?.long ??
        humanize(field);

    // Head-row field, e.g. `subtotal`.
    if (segments.length < 3) return fieldLabel;

    // Child field, e.g. `order_sale_products.754.kilo_price`.
    const collection = segments[0];
    const rowId = segments[1];
    const collectionLabel =
        lookupCollectionLabel(collection)?.long ?? humanize(collection);
    const rowLabel = rowLabels[`${collection}.${rowId}`] ?? `#${rowId}`;

    return `${collectionLabel} ${rowLabel} · ${fieldLabel}`;
}

/**
 * A number that identifies rather than measures: `1042` is a sale's code, not
 * one thousand and forty-two, so it must never be grouped. Everything else
 * numeric is a quantity or an amount and reads better with separators.
 *
 * Recognised by name because nothing in the value itself distinguishes the two,
 * and the naming is consistent across the schema: `id`, `<x>_id`, `<x>_code`.
 */
function isIdentifierColumn(field: string): boolean {
    return field === 'id' || /_(id|code)$/.test(field);
}

// A few legacy MySQL flags arrive in snapshots as 0/1 even though the field
// is semantically a checkbox. They must render as text (No/Sí), not as a
// grouped number. Ordinary numeric fields are intentionally not included:
// their value alone cannot tell us whether 1 means "one" or "Sí".
const NUMERIC_BOOLEAN_COLUMNS = new Set(['is_inactive', 'is_leader']);

function isNumericBooleanColumn(field?: string): boolean {
    return !!field && NUMERIC_BOOLEAN_COLUMNS.has(field);
}

function formatBooleanLike(value: unknown): string | undefined {
    if (value === true || value === 1 || value === '1') return 'Sí';
    if (value === false || value === 0 || value === '0') return 'No';
    return undefined;
}

/**
 * Thousands separators, keeping the value's OWN precision: `12529.28` ->
 * `12,529.28`, `12500` -> `12,500`, `12529.283` -> `12,529.283`.
 *
 * Deliberately NOT the house `formatFloat`, which is wrong for an audit twice
 * over: it forces `toFixed(2)`, which would silently drop the third decimal of
 * a kilo figure, and it rounds anything under 0.01 down to zero — so a real
 * change from `0.005` to `0` would render as "0 → 0", a row asserting that
 * nothing changed while being listed as a change. The grouping regex is the
 * same one, so the two agree on everything they both handle.
 */
function groupThousands(value: number | string): string {
    const text = String(value);
    // Exponential notation has no grouping to do and the regex would mangle it.
    if (text.indexOf('e') !== -1 || text.indexOf('E') !== -1) return text;

    const dot = text.indexOf('.');
    const integerPart = dot === -1 ? text : text.slice(0, dot);
    const fraction = dot === -1 ? '' : text.slice(dot);

    return (
        integerPart.replace(/\B(?=(\d{3})+(?!\d))/g, ',') + fraction
    );
}

/**
 * Renders a scalar snapshot value for display.
 *
 * `field` is the column the value came from — the last segment of the identity
 * path. Without it a number cannot be formatted correctly, because a code and
 * an amount are the same JSON number.
 */
export function formatChangeValue(value: unknown, field?: string): string {
    if (value === null || value === undefined) return '—';
    if (isNumericBooleanColumn(field)) {
        return formatBooleanLike(value) ?? String(value);
    }
    if (typeof value === 'boolean') return value ? 'Sí' : 'No';
    if (isNumericLike(value) && (!field || !isIdentifierColumn(field))) {
        return groupThousands(value);
    }
    return String(value);
}

/** Both rendered sides of one diff row, plus how the pair should be aligned. */
export interface FormattedChangePair {
    old: string;
    new: string;
    /** The raw value behind each side, for a tooltip; absent when unlabelled. */
    oldTitle?: string;
    newTitle?: string;
    /** Whether the rendered Antes value should use numeric alignment. */
    oldIsNumeric: boolean;
    /** Whether the rendered Después value should use numeric alignment. */
    newIsNumeric: boolean;
}

/**
 * Renders both sides of a change together, because a foreign key cannot be
 * formatted one side at a time.
 *
 * When a label is known the id is replaced by the name, and the id survives as
 * a tooltip — an auditor occasionally needs it, and it is the only thing that
 * still identifies a row whose name has since changed.
 *
 * The asymmetric case is the one worth the shared function: if only one side
 * resolves — the other points at a row the snapshot never captured, or predates
 * the relation being included — the unlabelled side renders `#2` rather than a
 * bare `2`, so "Entregado → #1" reads as a missing name instead of implying the
 * status became the number one.
 *
 * A labelled pair is text, so it left-aligns under the house convention even
 * though both underlying values are numbers.
 */
export function formatChangePair(
    oldValue: unknown,
    newValue: unknown,
    labels?: ValueLabelPair,
    path?: string,
): FormattedChangePair {
    const oldLabel = labels?.old;
    const newLabel = labels?.new;

    // The column, not the whole path: `order_sale_products.812.kilos` formats
    // by `kilos`, exactly as the head row's own `kilos` would.
    const field = path ? path.split('.').pop() : undefined;

    if (!oldLabel && !newLabel) {
        return {
            old: formatChangeValue(oldValue, field),
            new: formatChangeValue(newValue, field),
            oldIsNumeric: isNumericChangeValue(oldValue, field),
            newIsNumeric: isNumericChangeValue(newValue, field),
        };
    }

    const render = (value: unknown, label: string | undefined) => {
        if (label) return label;
        if (value === null || value === undefined) return '—';
        return `#${String(value)}`;
    };

    return {
        old: render(oldValue, oldLabel),
        new: render(newValue, newLabel),
        oldTitle:
            oldLabel && oldValue !== null && oldValue !== undefined
                ? String(oldValue)
                : undefined,
        newTitle:
            newLabel && newValue !== null && newValue !== undefined
                ? String(newValue)
                : undefined,
        // Once either side has a relation label, both rendered values are
        // textual (the unlabelled side is rendered as `#<id>`). Align the
        // pair as text rather than using the raw numeric foreign-key values.
        oldIsNumeric: false,
        newIsNumeric: false,
    };
}

/**
 * Numbers are right-aligned, text left-aligned — the house table convention.
 * Booleans render as Sí/No, so they count as text.
 */
export function isNumericChangeValue(
    value: unknown,
    field?: string,
): boolean {
    return !isNumericBooleanColumn(field) && isNumericLike(value);
}

function isNumericLike(value: unknown): value is number | string {
    if (typeof value === 'number') return Number.isFinite(value);
    if (typeof value !== 'string' || value.trim() === '') return false;
    return /^[-+]?(?:\d+\.?\d*|\.\d+)$/.test(value.trim());
}
